<script>
  import { createEventDispatcher } from "svelte";
  import { fade, fly } from "svelte/transition";
  import Enterprise from "carbon-icons-svelte/lib/Enterprise.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import { Geocoder } from "@beyonk/svelte-mapbox";
  import { inlineAddress, parseMapboxAddress, portal } from "../../helpers.js";
  import PhoneInput from "../../components/PhoneInput.svelte";
  import Contacts from "../Contacts/Contacts.svelte";

  export let company = {};

  const dispatch = createEventDispatcher();

  $: if (company && !company.contacts) {
    company.contacts = company.people || [];
  }

  function onAddressChange(e) {
    const newAddress = e.detail.result;
    company.address = parseMapboxAddress(newAddress);
    dispatch("address-updated");
  }

  function save() {
    dispatch("company-saved");
  }

  function handleCancel() {
    dispatch("close");
  }

  function handleKeydown(e) {
    if (e.key === "Escape") {
      handleCancel();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  use:portal
  class="m3-dialog-scrim"
  role="presentation"
  on:click|self={handleCancel}
  transition:fade={{ duration: 150 }}
>
  <div
    class="company-dialog-surface"
    role="dialog"
    aria-modal="true"
    aria-labelledby="company-dialog-title"
    transition:fly={{ y: 20, duration: 220 }}
  >
    <!-- Modal Header -->
    <header class="m3-dialog-header">
      <div class="header-left-group">
        <div class="header-icon-badge">
          <Enterprise size={22} />
        </div>
        <div class="header-titles">
          <div class="title-row">
            <h3 id="company-dialog-title" class="dialog-title">
              {company.name ? company.name : "Company Details"}
            </h3>
            <span class="m3-badge">Company Record</span>
          </div>
          <p class="dialog-subtitle">
            Update organization details, registered office address, and key contacts
          </p>
        </div>
      </div>
      <button
        type="button"
        class="m3-close-btn"
        on:click={handleCancel}
        title="Close (Esc)"
        aria-label="Close dialog"
      >
        <Close size={18} />
      </button>
    </header>

    <!-- Modal Body -->
    <div class="m3-dialog-body">
      <!-- Section 1: Company Profile -->
      <div class="form-grid-2">
        <div class="m3-field">
          <label class="m3-field-label" for="company-name-input">Company Name</label>
          <input
            id="company-name-input"
            type="text"
            class="m3-input"
            placeholder="e.g. Embedded Test Haulage Pty Ltd"
            bind:value={company.name}
          />
        </div>
        <div class="m3-field">
          <label class="m3-field-label" for="company-phone-input">Phone</label>
          <PhoneInput
            id="company-phone-input"
            placeholder="0412 345 678"
            bind:value={company.phone}
          />
        </div>
      </div>

      <!-- Section 2: Registered Address -->
      <div class="m3-field address-field">
        <label class="m3-field-label" for="registered-address-input">Registered Address</label>
        <div class="geocoder-embed">
          <Geocoder
            accessToken={MAPBOX_TOKEN}
            value={inlineAddress(company.address)}
            on:result={onAddressChange}
            geocoder
            options={{ countries: "AU" }}
          />
        </div>
      </div>

      <!-- Section 3: Tax Identification Numbers (ABN / ACN) -->
      <div class="form-grid-2">
        <div class="m3-field">
          <label class="m3-field-label" for="company-abn-input">ABN</label>
          <input
            id="company-abn-input"
            type="text"
            class="m3-input"
            placeholder="11-digit Australian Business Number"
            bind:value={company.ABN}
          />
        </div>
        <div class="m3-field">
          <label class="m3-field-label" for="company-acn-input">ACN</label>
          <input
            id="company-acn-input"
            type="text"
            class="m3-input"
            placeholder="9-digit Australian Company Number"
            bind:value={company.ACN}
          />
        </div>
      </div>

      <!-- Section 4: Company Contacts -->
      <div class="company-contacts-section">
        <Contacts bind:contacts={company.contacts} />
      </div>
    </div>

    <!-- Modal Footer -->
    <footer class="m3-dialog-footer">
      <div class="footer-left">
        <span class="status-indicator"></span>
        <span class="footer-note">Organization record is synchronized to database</span>
      </div>
      <div class="footer-actions">
        <button type="button" class="btn-cancel" on:click={handleCancel}>
          Cancel
        </button>
        <button type="button" class="btn-save" on:click={save}>
          <Checkmark size={15} />
          <span>Save</span>
        </button>
      </div>
    </footer>
  </div>
</div>

<style>
  /* Scrim Backdrop */
  .m3-dialog-scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.65);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    z-index: 9999;
  }

  /* Modal Surface Card */
  .company-dialog-surface {
    width: 780px;
    max-width: 96vw;
    max-height: 90vh;
    background: #ffffff;
    border-radius: 16px;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.2),
      0 10px 10px -5px rgba(0, 0, 0, 0.1),
      0 0 0 1px rgba(226, 232, 240, 0.9);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: #1e293b;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  }

  /* Header */
  .m3-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 22px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
  }

  .header-left-group {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .header-icon-badge {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(2, 132, 199, 0.35);
    flex-shrink: 0;
  }

  .header-titles {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .dialog-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .m3-badge {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 7px;
    border-radius: 9999px;
    background: #e0f2fe;
    color: #0369a1;
    border: 1px solid #bae6fd;
  }

  .dialog-subtitle {
    margin: 0;
    font-size: 12px;
    color: #64748b;
  }

  .m3-close-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .m3-close-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #cbd5e1;
  }

  /* Body */
  .m3-dialog-body {
    padding: 18px 22px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: #ffffff;
    flex: 1;
  }

  .form-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .m3-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
  }

  .m3-field-label {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .m3-input {
    height: 34px;
    padding: 0 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 12.5px;
    color: #1e293b;
    background: #ffffff;
    outline: none;
    transition: all 0.15s ease;
    width: 100%;
    box-sizing: border-box;
  }

  .m3-input:focus {
    border-color: #0284c7;
    box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
  }

  .m3-input::placeholder {
    color: #94a3b8;
  }

  .address-field {
    width: 100%;
  }

  /* Geocoder embed styles */
  .geocoder-embed {
    width: 100%;
    position: relative;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder) {
    width: 100% !important;
    max-width: 100% !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 6px !important;
    box-shadow: none !important;
    height: 34px !important;
    min-height: 34px !important;
    font-size: 12.5px !important;
    background-color: #ffffff !important;
    transition: all 0.15s ease !important;
    display: flex !important;
    align-items: center !important;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder:focus-within) {
    border-color: #0284c7 !important;
    box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15) !important;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder--input) {
    height: 32px !important;
    padding: 0 10px 0 32px !important;
    font-size: 12.5px !important;
    color: #1e293b !important;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder--icon-search) {
    top: 8px !important;
    left: 8px !important;
    fill: #94a3b8 !important;
  }

  .company-contacts-section {
    margin-top: 4px;
    width: 100%;
  }

  /* Footer */
  .m3-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 22px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
  }

  .footer-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .status-indicator {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  }

  .footer-note {
    font-size: 11.5px;
    color: #64748b;
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .btn-cancel {
    padding: 7px 16px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #475569;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-cancel:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }

  .btn-save {
    padding: 7px 18px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: #0284c7;
    color: #ffffff;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 1px 2px rgba(2, 132, 199, 0.25);
    transition: all 0.15s ease;
  }

  .btn-save:hover {
    background: #0369a1;
    box-shadow: 0 2px 4px rgba(2, 132, 199, 0.35);
  }

  .btn-save:active {
    transform: scale(0.98);
  }
</style>